/* =============================================================================
   Saved-search criteria v2 (PlatexAPI SavedSearchCriteria) <-> the builder's
   choices. The API owns validation and the plain-English summary; this file
   only maps the form both ways and writes the card's detail line.
   Docs: docs/account/SAVED_SEARCH_ALERTS_PLAN.md (steps 2, 4a, 5).
   ========================================================================== */

import { parseFormatToken } from 'src/app/components/classification-pill/format-token';

export type BuilderStyle = 'any' | 'dateless' | 'current' | 'prefix' | 'suffix' | 'ni';
export type BuilderFrequency = 'weekly' | 'daily' | 'instant' | 'none';

export interface BuilderChoices {
  style: BuilderStyle;
  formats: string[];        // dateless only, e.g. ['1x3', '2x2', '3x1']
  characters: number[];     // prefix / suffix only: 5, 6, 7 (all or none = any length)
  maxYear: string;          // current only: '' = any year, else e.g. '2019'
  term: string;
  minPounds: string;        // as typed; '' = none
  maxPounds: string;
  includeAuctions: boolean;
  frequency: BuilderFrequency;
}

export const DEFAULT_CHOICES: BuilderChoices = {
  style: 'dateless',
  formats: ['1x3', '2x2', '3x1'],
  characters: [],
  maxYear: '',
  term: '',
  minPounds: '',
  maxPounds: '',
  includeAuctions: true,
  frequency: 'weekly',
};

/** Every alert scope; "auction" is the Include auction lots switch (owner decision 2026-10-23). */
const SCOPES_WITHOUT_AUCTIONS = ['new', 'price_reductions', 'newly_available'];

export function alertScopeFor(choices: BuilderChoices): string[] {
  return choices.includeAuctions ? [...SCOPES_WITHOUT_AUCTIONS, 'auction'] : SCOPES_WITHOUT_AUCTIONS;
}

export function toPence(pounds: string): number | null {
  const digits = (pounds || '').replace(/[^0-9]/g, '');
  return digits ? Number(digits) * 100 : null;
}

export function toCriteriaJson(choices: BuilderChoices): string {
  const c: { [key: string]: any } = {};
  if (choices.style === 'dateless') {
    c['style'] = ['dateless'];
    if (choices.formats.length) c['formats'] = choices.formats;
  } else if (choices.style === 'current') {
    c['style'] = ['current'];
    if (choices.maxYear) c['maxYear'] = Number(choices.maxYear);
  } else if (choices.style === 'prefix' || choices.style === 'suffix') {
    c['style'] = [choices.style];
    const n = [...choices.characters].sort();
    if (n.length && n.length < 3) c['characters'] = n;
  } else if (choices.style === 'ni') c['style'] = ['ni'];
  const term = (choices.term || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (term) c['term'] = term;
  const min = toPence(choices.minPounds);
  const max = toPence(choices.maxPounds);
  if (min != null) c['priceMin'] = min;
  if (max != null) c['priceMax'] = max;
  return JSON.stringify(c);
}

function parse(criteriaJson: string): { [key: string]: any } {
  try {
    const c = JSON.parse(criteriaJson || '{}');
    return c && typeof c === 'object' ? c : {};
  } catch {
    return {};
  }
}

function list(value: any): string[] {
  if (Array.isArray(value)) return value.filter((v) => typeof v === 'string');
  return typeof value === 'string' && value ? [value] : [];
}

/** A saved search back into the builder (edit). */
export function fromSavedSearch(criteriaJson: string, alertScope: string[], frequency: string): BuilderChoices {
  const c = parse(criteriaJson);
  const styles = list(c['style']).concat(list(c['format']));
  const formats = list(c['formats']).filter((f) => !!parseFormatToken(f)).map((f) => parseFormatToken(f)!.token);
  let style: BuilderStyle = 'any';
  if (styles.includes('dateless') || formats.length) style = 'dateless';
  else if (styles.includes('current')) style = 'current';
  else if (styles.includes('prefix')) style = 'prefix';
  else if (styles.includes('suffix')) style = 'suffix';
  else if (styles.includes('ni')) style = 'ni';
  const pounds = (pence: any) => (typeof pence === 'number' && pence > 0 ? Math.round(pence / 100).toLocaleString('en-GB') : '');
  return {
    style,
    formats,
    characters: Array.isArray(c['characters']) ? c['characters'].filter((n: any) => typeof n === 'number') : [],
    maxYear: typeof c['maxYear'] === 'number' ? String(c['maxYear']) : '',
    term: typeof c['term'] === 'string' ? c['term'] : '',
    minPounds: pounds(c['priceMin']),
    maxPounds: pounds(c['priceMax']),
    includeAuctions: (alertScope || []).includes('auction'),
    frequency: (['weekly', 'daily', 'instant', 'none'].includes(frequency) ? frequency : 'weekly') as BuilderFrequency,
  };
}

/** The card's second line: "1×3, 2×2 and 3×1 · for sale and at auction · all-in price". */
export function criteriaDetails(criteriaJson: string, alertScope: string[]): string {
  const c = parse(criteriaJson);
  const parts: string[] = [];
  const formats = list(c['formats']).map((f) => parseFormatToken(f)).filter((f) => !!f).map((f) => f!.display);
  const styles = list(c['style']).concat(list(c['format']));
  if (formats.length) parts.push(formats.length === 1 ? formats[0] : formats.slice(0, -1).join(', ') + ' and ' + formats[formats.length - 1]);
  else if (!styles.length) parts.push('Any style');
  const chars: number[] = Array.isArray(c['characters']) ? c['characters'] : [];
  if (chars.length) parts.push(chars.length === 1 ? `${chars[0]} characters` : chars.slice(0, -1).join(', ') + ` or ${chars[chars.length - 1]} characters`);
  if (typeof c['maxYear'] === 'number') parts.push(`dated ${c['maxYear']} or earlier`);
  if (typeof c['term'] === 'string' && c['term']) parts.push('reads almost exactly');
  parts.push((alertScope || []).includes('auction') ? 'for sale and at auction' : 'for sale');
  if (c['priceMin'] != null || c['priceMax'] != null) parts.push('all-in price');
  return parts.join(' · ');
}

export function hasTerm(criteriaJson: string): boolean {
  const t = parse(criteriaJson)['term'];
  return typeof t === 'string' && t.length > 0;
}

/** The word a search reads as, if it has one. */
export function termOf(criteriaJson: string): string | null {
  const t = parse(criteriaJson)['term'];
  return typeof t === 'string' && t.length > 0 ? t : null;
}

/** The styles a search is limited to (none = any style). */
export function styleList(criteriaJson: string): string[] {
  return list(parse(criteriaJson)['style']);
}
